import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiWifi, FiCar, FiUmbrella, FiCoffee, FiWind, FiDroplet, FiUsers, FiShield } = FiIcons;

const Facilities = () => {
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  const facilities = [
    {
      icon: FiDroplet,
      title: 'Swimming Pool',
      description: 'Cool off after your safari adventure in our refreshing outdoor pool.',
      rating: '8.7/10'
    },
    {
      icon: FiCoffee,
      title: 'Restaurant & Bar',
      description: 'Authentic Sri Lankan cuisine and international dishes with safari views.',
      rating: '8.5/10'
    },
    {
      icon: FiWifi,
      title: 'Free Wi-Fi',
      description: 'Stay connected throughout the property with complimentary high-speed internet.',
      rating: '8.3/10'
    },
    {
      icon: FiCar,
      title: 'Free Parking',
      description: 'Secure parking for your vehicle right at the hotel entrance.',
      rating: '9.0/10'
    },
    {
      icon: FiWind,
      title: 'Air Conditioning',
      description: 'All rooms feature reliable AC to ensure comfort after hot safari days.',
      rating: '8.6/10'
    },
    {
      icon: FiUmbrella,
      title: 'Outdoor Terrace',
      description: 'Relax on our terrace with views of the surrounding wilderness.',
      rating: '8.4/10'
    },
    {
      icon: FiUsers,
      title: 'Safari Desk',
      description: 'On-site Niluka Safari booking and information center.',
      rating: '9.2/10'
    },
    {
      icon: FiShield,
      title: '24/7 Security',
      description: 'Round-the-clock security ensuring your safety and peace of mind.',
      rating: '9.1/10'
    }
  ];

  const services = [
    'Early morning safari departures (5:30 AM)',
    'Safari gear storage and cleaning',
    'Laundry and dry cleaning service',
    'Currency exchange assistance',
    'Tour planning and local recommendations',
    'Airport transfer arrangements',
    'Equipment rental (binoculars, cameras)',
    'Emergency medical assistance'
  ];

  return (
    <section id="facilities" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-900 mb-6">
            Premium <span className="gradient-text">Facilities</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-inter">
            Everything you need for the perfect safari basecamp experience. 
            Rated 8.6/10 for facilities and comfort by our guests.
          </p>
        </motion.div>

        {/* Facilities Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {facilities.map((facility, index) => (
            <motion.div
              key={facility.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="glass-effect rounded-2xl p-6 text-center hover-lift group"
            >
              <div className="w-14 h-14 liquid-gradient rounded-full flex items-center justify-center mx-auto mb-4 morphing-blob group-hover:scale-110 transition-transform duration-300">
                <SafeIcon icon={facility.icon} className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-playfair font-bold text-gray-900 mb-2">
                {facility.title}
              </h3>
              <p className="text-gray-600 text-sm mb-3 font-inter">
                {facility.description}
              </p>
              <div className="text-orange-600 font-semibold text-sm">
                Guest Rating: {facility.rating}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Services Section */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <h3 className="text-3xl font-playfair font-bold text-gray-900 mb-6">
              Safari-Focused Services
            </h3>
            <p className="text-gray-600 mb-8 font-inter leading-relaxed">
              We understand the unique needs of safari travelers. Our services are designed 
              to enhance your wildlife experience from arrival to departure.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {services.map((service, index) => (
                <motion.div
                  key={service}
                  initial={{ opacity: 0, x: -20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: 1.0 + index * 0.05 }}
                  className="flex items-center space-x-3"
                >
                  <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0" />
                  <span className="text-gray-700 font-inter text-sm">{service}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="relative"
          >
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=300&h=400&fit=crop"
                alt="Hotel pool"
                className="w-full h-48 object-cover rounded-2xl hover-lift"
              />
              <img
                src="https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=300&h=400&fit=crop"
                alt="Hotel restaurant"
                className="w-full h-48 object-cover rounded-2xl hover-lift mt-8"
              />
            </div>
            
            {/* Rating Badge */}
            <motion.div
              className="absolute -bottom-6 -left-6 glass-effect p-4 rounded-xl text-center"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.8, delay: 1.2 }}
            >
              <div className="text-2xl font-bold text-orange-600">8.6</div>
              <div className="text-gray-700 text-sm font-inter">Facilities Rating</div>
            </motion.div>
          </motion.div>
        </div>

        {/* Location Advantage */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.4 }}
          className="mt-16 liquid-gradient rounded-3xl p-8 text-white text-center"
        >
          <h3 className="text-3xl font-playfair font-bold mb-4">
            Unbeatable Location Advantage
          </h3>
          <p className="text-xl mb-6 opacity-90 max-w-3xl mx-auto">
            Located directly opposite the Udawalawe Elephant Transit Home, 
            we offer the closest accommodation to both the park entrance and the famous elephant sanctuary.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="text-3xl font-bold mb-2">0 meters</div>
              <div className="text-lg opacity-90">From Elephant Transit Home</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">2 minutes</div>
              <div className="text-lg opacity-90">Drive to Park Entrance</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">5:30 AM</div>
              <div className="text-lg opacity-90">First Safari Departure</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Facilities;